import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { observer } from "mobx-react";
import { RejectEscalatedCase } from "Apollo";
import { CaseManagementStore, ShowNotice } from "Store";
import Linkify from "react-linkify";
import Avatar from "module-common/Avatar";
import Icon from "module-common/Icon";
import TextArea from "module-common/TextArea";
import BubbleOption from "./BubbleOptionWithStoreMutation";

export default observer(
  class Bubble extends Component {
    static propTypes = {
      id: PropTypes.number,
      content: PropTypes.string,
      status: PropTypes.string,
      time: PropTypes.object,
      avatar: PropTypes.string,
      imageUrl: PropTypes.string,
      rejectionNote: PropTypes.string
    };

    render() {
      const store = CaseManagementStore;
      const { editPending, rejectPending, selectedTab } = store;
      const {
        id,
        content,
        status,
        time,
        avatar,
        imageUrl,
        rejectionNote
      } = this.props;
      const isEditing = editPending.id === id;
      const isRejecting = rejectPending.id === id;
      const isRejected = status === "REJECTED";
      const isPendingTab = selectedTab === "pending";
      const showOption = selectedTab !== "all";

      return (
        <Wrapper>
          <InnerWrapper>
            <ContentBubble isEditing={isEditing} isRejected={isRejected}>
              <Status>{status}</Status>
              {imageUrl && (
                <Image
                  url={imageUrl}
                  onClick={() => {
                    window.open(imageUrl, "_blank");
                  }}
                />
              )}
              <Content>
                <Linkify>{content}</Linkify>
              </Content>
              {isRejecting && (
                <Rejection>
                  <RejectionHeader>
                    REJECTION REASON
                    <Icon
                      name="x-button"
                      width={10}
                      height={10}
                      onClick={() => {
                        store.rejectPending = { id: -1, content: "" };
                      }}
                    />
                  </RejectionHeader>
                  <TextArea
                    placeholder="Write your notes here..."
                    value={rejectPending.content}
                    onChange={event => {
                      store.rejectPending.content = event.target.value;
                    }}
                  />
                  <RejectButton
                    disabled={!rejectPending.content}
                    onClick={this.onReject}
                  >
                    Send Back
                  </RejectButton>
                </Rejection>
              )}
              {!isPendingTab &&
                isRejected && (
                  <Rejection>
                    <RejectionHeader>REJECTION REASON</RejectionHeader>
                    <RejectionNote>{rejectionNote}</RejectionNote>
                  </Rejection>
                )}
              {showOption && (
                <StyledBubbleOption
                  id={id}
                  content={content}
                  canEdit={!isPendingTab}
                  canDelete={!isPendingTab}
                  canApprove={isPendingTab}
                  canDisapprove={isPendingTab}
                />
              )}
            </ContentBubble>
            <AvatarSpace>
              {avatar && <Avatar image={avatar} isSmall />}
            </AvatarSpace>
          </InnerWrapper>
          {time && <Time>{time.format("LLL")}</Time>}
        </Wrapper>
      );
    }

    onReject = async () => {
      const store = CaseManagementStore;
      const { rejectPending: { id, content } } = store;
      try {
        await RejectEscalatedCase(id, content);
        store.rejectPending = { id: -1, content: "" };
        ShowNotice("pending-reject-success");
      } catch (error) {
        ShowNotice("pending-reject-error");
      }
    };
  }
);

const Wrapper = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  margin-bottom: 6px;
`;
const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const AvatarSpace = styled.div`
  display: flex;
  justify-content: center;
  flex: none;
  width: 40px;
  margin: 0 5px;
`;
const Time = styled.span`
  margin-top: 4px;
  font-size: 0.7rem;
  margin-right: 40px;
  align-self: flex-end;
`;
const ContentBubble = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  max-width: 300px;
  font-size: 0.9rem;
  border: none;
  background-color: ${props => {
    if (props.isEditing) {
      return props.theme.editingPendingStream;
    } else if (props.isRejected) {
      return props.theme.rejectedPendingStream;
    } else {
      return props.theme.normalPendingStream;
    }
  }};
  &:after {
    content: " ";
    position: absolute;
    border: 6px solid;
    top: 25px;
    right: -12px;
    border-color: transparent transparent transparent
      ${props => {
        if (props.isEditing) {
          return props.theme.editingPendingStream;
        } else if (props.isRejected) {
          return props.theme.rejectedPendingStream;
        } else {
          return props.theme.normalPendingStream;
        }
      }};
  }
`;
const Status = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: white;
  padding: 10px 20px 0px 20px;
`;
const Content = styled.div`
  color: white;
  padding: 0px 20px 10px 20px;
`;
const Rejection = styled.div`
  padding: 5px 10px 5px 10px;
  display: flex;
  flex-direction: column;
  border-radius: 0px 0px 8px 8px;
  background-color: ${props => props.theme.rejectedPendingStream};
  border-top: dashed 1px ${props => props.theme.panelBorder};
`;
const RejectionHeader = styled.div`
  display: flex;
  padding: 0px 0px 2px 10px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  font-size: 0.9em;
  color: white;
`;
const RejectionNote = styled.div`
  padding: 0px 10px 4px 10px;
  color: white;
`;
const RejectButton = styled.button`
  display: flex;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: none;
  border-radius: 20px;
  color: #2c8ac8;
  transition: 0.3s;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 2px 10px;
  margin: 6px 0px 4px 0px;
  background-color: white;
  &:focus {
    outline: none;
  }
  &:hover {
    opacity: 0.7;
  }
  ${props =>
    props.disabled &&
    css`
      border: 1px solid white;
      background-color: transparent;
      color: white;
    `};
`;
const StyledBubbleOption = styled(BubbleOption)`
  position: absolute;
  top: 2px;
  left: 4px;
`;
const Image = styled.div`
  width: 260px;
  height: 100px;
  margin-bottom: 4px;
  cursor: pointer;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  &:hover {
    opacity: 0.7;
  }
  ${props =>
    props.url &&
    css`
      background-image: url(${props.url});
    `};
`;



// WEBPACK FOOTER //
// ./src/module-case-management/content-panel/approval-list/bubble/BubbleWithStore.js